import { useCallback, useEffect, useState } from 'react';
import { isDocDefaultInherited } from '../../../../utils/variableScope';

const inheritedKey = (variable) =>
  `${variable?.sourceId || ''}:${variable?.id || ''}`;

const valuesEqual = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    const aa = Array.isArray(a) ? a : [];
    const bb = Array.isArray(b) ? b : [];
    if (aa.length !== bb.length) return false;
    return aa.every((v, i) => String(v ?? '') === String(bb[i] ?? ''));
  }
  return String(a ?? '') === String(b ?? '');
};

/**
 * Gate editing of ancestor-owned (inherited) variable values behind a
 * one-time confirmation for the panel session.
 */
export default function useInheritedVariableEdit(
  onPatchInheritedVariable,
  inheritedRows = [],
  onClaimDocVariable = null
) {
  const canClaimDocDefaults = typeof onClaimDocVariable === 'function';
  const canEditInherited =
    typeof onPatchInheritedVariable === 'function' || canClaimDocDefaults;
  const [pending, setPending] = useState(null);
  const [unlockedKeys, setUnlockedKeys] = useState(() => new Set());
  const [focusKey, setFocusKey] = useState(null);
  /** Optimistic patch overlays until allSprints catches up. */
  const [draftPatches, setDraftPatches] = useState(() => new Map());

  const isUnlocked = useCallback(
    (variable) => unlockedKeys.has(inheritedKey(variable)),
    [unlockedKeys]
  );

  const requestEdit = useCallback(
    (variable) => {
      if (!canEditInherited || !variable) return;
      if (unlockedKeys.has(inheritedKey(variable))) return;
      // Doc defaults claim onto this campaign — no parent-campaign confirm.
      if (canClaimDocDefaults && isDocDefaultInherited(variable)) {
        const key = inheritedKey(variable);
        setUnlockedKeys((prev) => {
          const next = new Set(prev);
          next.add(key);
          return next;
        });
        setFocusKey(key);
        return;
      }
      if (typeof onPatchInheritedVariable !== 'function') return;
      setPending(variable);
    },
    [canEditInherited, canClaimDocDefaults, onPatchInheritedVariable, unlockedKeys]
  );

  const confirmEdit = useCallback(() => {
    if (!pending) return;
    const key = inheritedKey(pending);
    setUnlockedKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    setFocusKey(key);
    setPending(null);
  }, [pending]);

  const cancelEdit = useCallback(() => {
    setPending(null);
  }, []);

  const clearFocus = useCallback(() => {
    setFocusKey(null);
  }, []);

  const patchValue = useCallback(
    (variable, patchOrValue, opts) => {
      const claimDoc = canClaimDocDefaults && isDocDefaultInherited(variable);
      if (!variable?.id || (!claimDoc && !variable.sourceId)) return;
      // Back-compat: string → { value } (older call sites)
      const patch =
        patchOrValue != null &&
        typeof patchOrValue === 'object' &&
        !Array.isArray(patchOrValue)
          ? patchOrValue
          : { value: patchOrValue };
      const key = inheritedKey(variable);
      setDraftPatches((prev) => {
        const next = new Map(prev);
        next.set(key, { ...(prev.get(key) || {}), ...patch });
        return next;
      });
      if (claimDoc) {
        onClaimDocVariable?.(variable, patch, opts);
        return;
      }
      onPatchInheritedVariable?.(variable.sourceId, variable.id, patch, opts);
    },
    [canClaimDocDefaults, onClaimDocVariable, onPatchInheritedVariable]
  );

  const displayVariable = useCallback(
    (variable) => {
      const key = inheritedKey(variable);
      if (!draftPatches.has(key)) return variable;
      return { ...variable, ...draftPatches.get(key) };
    },
    [draftPatches]
  );

  // Doc defaults are this campaign's to fill — unlock immediately so the
  // value cell is editable without a parent-campaign confirm.
  useEffect(() => {
    if (!canClaimDocDefaults) return;
    setUnlockedKeys((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const row of inheritedRows) {
        if (!isDocDefaultInherited(row)) continue;
        const key = inheritedKey(row);
        if (!next.has(key)) {
          next.add(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [canClaimDocDefaults, inheritedRows]);

  // Drop overlays once the live inherited row matches (or the row is gone).
  useEffect(() => {
    if (draftPatches.size === 0) return;
    setDraftPatches((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [key, draft] of prev) {
        const live = inheritedRows.find((v) => inheritedKey(v) === key);
        if (!live) {
          next.delete(key);
          changed = true;
          continue;
        }
        const liveMatch = Object.keys(draft).every((field) =>
          valuesEqual(live[field], draft[field])
        );
        if (liveMatch) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [inheritedRows, draftPatches.size]);

  return {
    canEditInherited,
    pending,
    focusKey,
    isUnlocked,
    requestEdit,
    confirmEdit,
    cancelEdit,
    clearFocus,
    patchValue,
    displayVariable,
    keyOf: inheritedKey,
  };
}
